// src/Components/SideNav.js
import React, { useState } from 'react';
import { useAuth } from '../Contexts/AuthContext';

export default function SideNav({ isOpen, onClose, onOpenPaywall, onOpenFleetInfo, onOpenModelInfo }) {
  const { user, isPremium, signInWithGoogle, logout, createPortalLink } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleAuth = async () => {
    try {
      if (user) {
        await logout();
      } else {
        await signInWithGoogle();
      }
      onClose();
    } catch (error) {
      console.error('Authentication error:', error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      // Open window immediately to avoid Safari blocking
      const newWindow = window.open('about:blank', '_blank');

      const url = await createPortalLink();
      if (url && newWindow) {
        newWindow.location.href = url;
      } else if (!newWindow) {
        // Fallback: try opening directly (may still be blocked)
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleFleetInfo = () => {
    if (user) {
      onClose();
      onOpenFleetInfo();
    }
  };

  const handleModelInfo = () => {
    if (user) {
      onClose();
      onOpenModelInfo();
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[1500] transition-opacity"
          style={{ background: 'rgba(0,0,0,0.30)' }}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          fontFamily: "'DM Sans', sans-serif",
          width: '300px',
          boxShadow: '0 4px 32px rgba(0,0,0,0.12)',
        }}
        className={`fixed top-0 right-0 h-full bg-white z-[1500] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div
          style={{ height: '56px', borderBottom: '1px solid #f0f0f0' }}
          className="px-5 flex items-center justify-between flex-shrink-0"
        >
          <div className="flex items-center gap-2">
            {/* Plane icon */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1d4ed8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
            </svg>
            <span style={{ color: '#111111', fontSize: '14px', fontWeight: 600 }}>Menu</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            style={{ width: '32px', height: '32px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {user ? (
            <>
              {/* User Profile */}
              <div style={{ borderBottom: '1px solid #f0f0f0' }} className="p-4 flex items-center gap-3">
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="rounded-full flex-shrink-0"
                  style={{ width: '40px', height: '40px' }}
                />
                <div className="flex-1 min-w-0">
                  <p style={{ color: '#111111', fontSize: '14px', fontWeight: 600 }} className="truncate">{user.displayName}</p>
                  <p style={{ color: '#6b7280', fontSize: '12px' }} className="truncate">{user.email}</p>
                  {isPremium && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '4px',
                        fontSize: '10px',
                        fontWeight: 700,
                        background: '#fffbeb',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                        padding: '2px 8px',
                        borderRadius: '9999px',
                      }}
                    >
                      ★ Premium
                    </span>
                  )}
                </div>
              </div>

              {/* Upgrade CTA (logged in, non-premium) */}
              {!isPremium && (
                <div
                  style={{
                    margin: '16px 16px 0',
                    borderRadius: '12px',
                    padding: '16px',
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <p style={{ color: '#111111', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Unlock Premium</p>
                  <p style={{ color: '#6b7280', fontSize: '12px', marginBottom: '12px' }}>Get route analytics, extended data &amp; more</p>
                  <button
                    onClick={() => { onClose(); onOpenPaywall(); }}
                    style={{
                      width: '100%',
                      padding: '8px 0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#ffffff',
                      background: '#1d4ed8',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#c41230'}
                    onMouseLeave={e => e.currentTarget.style.background = '#1d4ed8'}
                  >
                    View Plans
                  </button>
                </div>
              )}

              {/* Nav items */}
              <nav style={{ margin: '12px 8px 0' }}>
                {/* Fleet Information */}
                <button
                  onClick={handleFleetInfo}
                  className="flex items-center gap-3 rounded-lg hover:bg-[#fafafa] transition-colors cursor-pointer w-full text-left"
                  style={{ padding: '10px 12px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px' }}>Fleet Information</span>
                </button>

                {/* Aircraft Models */}
                <button
                  onClick={handleModelInfo}
                  className="flex items-center gap-3 rounded-lg hover:bg-[#fafafa] transition-colors cursor-pointer w-full text-left"
                  style={{ padding: '10px 12px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px' }}>Aircraft Models</span>
                </button>

                {/* Manage Subscription (premium only) */}
                {isPremium && (
                  <button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="flex items-center gap-3 rounded-lg hover:bg-[#fafafa] transition-colors cursor-pointer w-full text-left"
                    style={{ padding: '10px 12px', opacity: portalLoading ? 0.6 : 1 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <span style={{ color: '#111111', fontSize: '14px' }}>
                      {portalLoading ? 'Loading...' : 'Manage Subscription'}
                    </span>
                  </button>
                )}

                {/* Support */}
                <a
                  href="mailto:hypacharge@gmail.com"
                  className="flex items-center gap-3 rounded-lg hover:bg-[#fafafa] transition-colors w-full text-left"
                  style={{ padding: '10px 12px', textDecoration: 'none' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px' }}>Support</span>
                </a>
              </nav>
            </>
          ) : (
            <>
              {/* Nav items — disabled for logged-out users */}
              <nav style={{ margin: '12px 8px 0' }}>
                {/* Fleet Information (disabled) */}
                <button
                  disabled
                  className="flex items-center gap-3 rounded-lg w-full text-left"
                  style={{ padding: '10px 12px', opacity: 0.4, cursor: 'not-allowed' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px', flex: 1 }}>Fleet Information</span>
                  {/* Lock icon */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </button>

                {/* Aircraft Models (disabled) */}
                <button
                  disabled
                  className="flex items-center gap-3 rounded-lg w-full text-left"
                  style={{ padding: '10px 12px', opacity: 0.4, cursor: 'not-allowed' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px', flex: 1 }}>Aircraft Models</span>
                  {/* Lock icon */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </button>

                {/* Support (always accessible) */}
                <a
                  href="mailto:hypacharge@gmail.com"
                  className="flex items-center gap-3 rounded-lg hover:bg-[#fafafa] transition-colors w-full text-left"
                  style={{ padding: '10px 12px', textDecoration: 'none' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span style={{ color: '#111111', fontSize: '14px' }}>Support</span>
                </a>
              </nav>

              {/* Sign in prompt */}
              <div
                style={{
                  margin: '16px 16px 0',
                  borderRadius: '12px',
                  padding: '16px',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                }}
              >
                <p style={{ color: '#6b7280', fontSize: '12px' }}>
                  Sign in to access fleet information and aircraft models
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }} className="flex-shrink-0">
          {user ? (
            <button
              onClick={handleAuth}
              className="w-full flex items-center justify-center gap-2 rounded-lg transition-colors"
              style={{
                padding: '10px 16px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                color: '#6b7280',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sign Out</span>
            </button>
          ) : (
            <button
              onClick={handleAuth}
              className="w-full flex items-center justify-center rounded-lg transition-colors"
              style={{
                gap: '10px',
                padding: '10px 16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                color: '#111111',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
              onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
            >
              {/* Google logo */}
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span>Continue with Google</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
